import React, { useRef, useState } from 'react'
import {
  DataGrid,
  DataGridEditableCell,
  DataGridRef,
  editableActions,
  editableColumn,
  useDataGridEditable,
} from '../../../core/component'
import { confirm, notify, translate } from '../../../core'
import { Button, Form, Input, Space, Tooltip } from 'antd'
import { DATA_GRID_NEW_KEY } from '../../../core/component/DateGrid/resolver'
import { BadRequestException, QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { PlusOutlined } from '@ant-design/icons'
import { contactCategoryService } from '../service'
import { useLazyQuery } from '@apollo/client'
import { ContactCategory, ContactCategoryEntry } from '../type/contact-category'
import { contactCategoryEntityToEntry, contactCategoryEntryToDto } from '../model-mapper/contact-category-mapper'
import { GetContactCategories } from '../graphql/contact-category-queries.graphql'
import { ContactCategoryFindRequest } from '../type/contact-category.query'

export const ContactCategoryList = () => {
  const dataGridRef = useRef<DataGridRef<ContactCategoryEntry>>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [find] = useLazyQuery<
    QueryPageableResponse<ContactCategory, 'contactCategoriesFind'>,
    QueryPageableRequest<ContactCategoryFindRequest>>(GetContactCategories)

  const onSave = async (key: React.Key, row: ContactCategoryEntry) => {
    setSaving(true)
    const dto = contactCategoryEntryToDto(row)

    try {
      if (key === DATA_GRID_NEW_KEY)
        await contactCategoryService.create(dto)
      else
        await contactCategoryService.update(row.id, dto)

      notify.success(
        translate('contact_category'),
        translate('save_contact_category_success_message'),
      )

      dataGridRef.current?.refresh()
      markAsCompleted()
    } catch (e) {
      if (e instanceof BadRequestException)
        notify.error(
          translate('wrong_message', 'contact_category'),
          [e.message].flat().join('<br/>'),
        )
    } finally {
      setSaving(false)
    }
  }

  const {
    form,
    editingKey,
    isEditing,
    cancel,
    edit,
    save,
    markAsCompleted,
  } = useDataGridEditable<ContactCategoryEntry>(dataGridRef, onSave)

  const handleRemove = async (entity: ContactCategoryEntry) => {
    if (!entity)
      return

    const result = await confirm(
      translate('remove_confirmation_message', 'contact_category', '?'),
    )

    if (!result)
      return

    await contactCategoryService.remove(entity.id)

    notify.success(
      translate('remove', 'contact_category'),
      translate('contact_category', 'remove_success_message'),
    )

    dataGridRef.current?.refresh()
  }


  return (
    <div className="p-2">
      <Space className="w-100 justify-content-end p-2">
        <Tooltip title={translate('add')}>
          <Button
            icon={<PlusOutlined/>}
            onClick={() => {
              const row = dataGridRef.current?.addRow()
              edit(row ?? {})
            }}>
          </Button>
        </Tooltip>
      </Space>

      <Form form={form} component={false}>
        <DataGrid<ContactCategoryEntry>
          ref={dataGridRef}
          columns={[
            {
              dataIndex: 'id',
              title: translate('id'),
              width: 200,
            },
            editableColumn({
              dataIndex: 'title',
              title: translate('title'),
              editableComponent: <Form.Item name="title" rules={[
                {
                  required: true,
                  message: translate('missing_field_message', 'title'),
                },
              ]}>
                <Input/>
              </Form.Item>,
              isEditing,
            }),
            editableActions(
              editingKey,
              saving,
              isEditing,
              cancel,
              edit,
              save,
              handleRemove,
            ),
          ]}
          fetch={async (request) => {
            const {data} = await find({
              variables: {
                request,
              },
            })

            const list = data?.contactCategoriesFind.data ?? []
            const count = data?.contactCategoriesFind.count ?? 0

            return {
              data: list.map(contactCategoryEntityToEntry),
              count,
            }
          }}
          components={{
            body: {
              cell: DataGridEditableCell,
            },
          }}

        />
      </Form>
    </div>
  )
}
